/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    string, func, arrayOf, shape, bool,
    object,
} from 'prop-types';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import mbpLogger from '@module-logger/mbp';

import getAutoSuggest from '../../../../gql/queries/getAutoSuggest';
import SearchDropdown from './SearchDropdown';
import useExperimentServiceTracking from '../../../../helpers/experimentService/useExperimentServiceTracking';
import { getPageType } from '../../../../../state/ducks/App/App-Selectors';

/**
 *
 * @param {*} str take the input from search box
 * @return string
 * @example input val test"s it will return test\"s
 *  @example input val \ it will return ' '
 */
const escapeDoubleQuotes = (str) => str.replace(/"/g, '\\"');

const ElasticSearchSuggestions = ({
    track, phrase, brand, autoSuggestExperimentsEnabled, clearSearch, concatSearchTerm, cleanTerm, targeting, experimentsService, targetingEnabled,
    isSearchBarBrandFilterEnabled, includeSiblingBrandSearch, setIncludeSiblingBrandSearch, handleSearchSubmit, searchUiTypeAheadEnabled, setSearchValue, onChangeValue, autoCompleteEnabled,
}) => {
    let showOnlySuggestions = false || !autoCompleteEnabled;
    let response = (
        <SearchDropdown
            cleanTerm={cleanTerm}
            concatSearchTerm={concatSearchTerm}
            track={track}
            clearSearch={clearSearch}
            phrase={phrase}
            suggestions={{}}
            isSearchBarBrandFilterEnabled={isSearchBarBrandFilterEnabled}
            includeSiblingBrandSearch={includeSiblingBrandSearch}
            setIncludeSiblingBrandSearch={setIncludeSiblingBrandSearch}
            handleSearchSubmit={handleSearchSubmit}
            searchUiTypeAheadEnabled={searchUiTypeAheadEnabled}
        />
    );
    if (!phrase && (isSearchBarBrandFilterEnabled || searchUiTypeAheadEnabled)) return response;
    if (!phrase) return <></>;
    const getPhraseData = phrase.replace(/\\/g, ' ');
    const FIND_SUGGESTIONS = getAutoSuggest({
        targetingEnabled, brand, phrase: encodeURIComponent(escapeDoubleQuotes(getPhraseData)), experimentsService, autoCompleteEnabled,
    });
    const { error, data } = useQuery(FIND_SUGGESTIONS, {
        variables: { ...(targetingEnabled ? { targeting } : {}), phrase },
        context: {
            api: experimentsService ? 'experiments' : 'aggregator',
        },
    });
    const { trackGA4Experiments, trackClickStreamExperiments  } = useExperimentServiceTracking();
    const pageType = useSelector(getPageType);
    const location = useLocation();

    // reset impression flag on page change to track impression once in a page
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.wuAutoSuggestImpressionFired = false;
        }
    }, [location.pathname]);

    // tracking impression event for experiment service
    useEffect(() => {
        if (data?.autoSuggest?.experiments?.length && typeof window !== 'undefined' && !window.wuAutoSuggestImpressionFired) {
            trackGA4Experiments({ experiments: data.autoSuggest.experiments, eventAction: 'Search Test' });
            trackClickStreamExperiments({
                experiments: data.autoSuggest.experiments,
                action: 'Impression',
                page: { type: pageType },
            });
            window.wuAutoSuggestImpressionFired = true;
        }
    }, [data?.autoSuggest?.experiments]);

    // Ensure that LD flag is an object and that it has keys
    if (typeof autoSuggestExperimentsEnabled === 'object' && Object.keys(autoSuggestExperimentsEnabled)?.length && data?.autoSuggest?.experiments?.length) {
        // Match LD flag campaign name to Salesforce Experiments campaign name
        const campaignNameFlagNameMatch = data?.autoSuggest?.experiments?.find((campaign) => autoSuggestExperimentsEnabled[campaign.campaignName]);
        // check if variant of the LD flags matches the experiments we will use certain features
        if (campaignNameFlagNameMatch && autoSuggestExperimentsEnabled[campaignNameFlagNameMatch?.campaignName]?.includes(campaignNameFlagNameMatch?.variant)) {
            // this is currently controlling that we will show only suggestions and not products in the dropdown
            showOnlySuggestions = true;
        }
    }
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            message: `Failed to fetch content data for: auto suggestions from elastic search(ElasticSearchSuggestions) on brand ${brand}`,
        });
    }

    if (data?.autoSuggest?.products?.length > 0 || data?.autoSuggest?.phrases?.length > 0) {
        response = (
            <SearchDropdown
                cleanTerm={cleanTerm}
                concatSearchTerm={concatSearchTerm}
                track={track}
                clearSearch={clearSearch}
                phrase={phrase}
                suggestions={data.autoSuggest}
                isSearchBarBrandFilterEnabled={isSearchBarBrandFilterEnabled}
                includeSiblingBrandSearch={includeSiblingBrandSearch}
                setIncludeSiblingBrandSearch={setIncludeSiblingBrandSearch}
                handleSearchSubmit={handleSearchSubmit}
                searchUiTypeAheadEnabled={searchUiTypeAheadEnabled}
                onChangeValue={onChangeValue}
                setSearchValue={setSearchValue}
                showOnlySuggestions={showOnlySuggestions || !autoCompleteEnabled}
                attributionToken={data?.autoSuggest?.attributionToken}
            />
        );
    }
    return response;
};

ElasticSearchSuggestions.propTypes = {
    phrase: string.isRequired,
    brand: string.isRequired,
    clearSearch: func.isRequired,
    track: func.isRequired,
    concatSearchTerm: func.isRequired,
    cleanTerm: func,
    targeting: arrayOf(shape({
        key: string,
        value: string,
    })),
    experimentsService: bool,
    targetingEnabled: bool,
    isSearchBarBrandFilterEnabled: bool,
    includeSiblingBrandSearch: bool.isRequired,
    setIncludeSiblingBrandSearch: func.isRequired,
    handleSearchSubmit: func,
    searchUiTypeAheadEnabled: bool,
    setSearchValue: func,
    onChangeValue: func,
    autoSuggestExperimentsEnabled: object,
    autoCompleteEnabled: bool,
};

ElasticSearchSuggestions.defaultProps = {
    cleanTerm: () => { },
    targeting: [],
    experimentsService: false,
    targetingEnabled: false,
    isSearchBarBrandFilterEnabled: false,
    handleSearchSubmit: () => { },
    searchUiTypeAheadEnabled: false,
    setSearchValue: () => { },
    onChangeValue: () => { },
    autoSuggestExperimentsEnabled: {},
    autoCompleteEnabled: false,
};

export default ElasticSearchSuggestions;
